import { type SyftFormData } from "../blocks/forms/types";
import { type Campaign } from "../common/event_types";
import { reportFormSubmit, startFormBinder } from "./formBinder";
import { observeDocuments } from "../common/domObserver";

/**
 * Adds campaign UTM parameters to forms as hidden inputs.
 * Checks for existing inputs to prevent duplication.
 */
function addCampaignToForm(doc: Document, campaign: Campaign): void {
  const forms = doc.querySelectorAll("form");
  forms.forEach((form) => {
    Object.entries(campaign).forEach(([name, value]) => {
      if (value == null) return;
      const inputName = `utm_${name}`;

      // Check if UTM input already exists to prevent duplication
      const existingInput = form.querySelector(
        `input[name="${inputName}"]`
      ) as HTMLInputElement | null;

      if (existingInput) {
        // Update existing input value
        existingInput.value = value;
      } else {
        // Create new hidden input
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = inputName;
        input.value = value;
        form.appendChild(input);
      }
    });
  });
}

export function formSubmits(
  callback: (_path: string, _data: SyftFormData, _destination?: URL) => void,
  formInputCallback?: (_path: string, _data: SyftFormData) => void,
  campaign?: Campaign
): () => void {
  // Save original submit method before any modifications
  const originalSubmit = HTMLFormElement.prototype.submit;

  // Create the document handler function
  const handleDoc = (doc: Document) => {
    // Start form binding for this document
    const cleanup = startFormBinder(doc, callback, formInputCallback);

    // Add campaign tracking if provided
    if (campaign != null) {
      addCampaignToForm(doc, campaign);
    }

    // Return cleanup function
    return cleanup;
  };

  // Override the form submit method
  HTMLFormElement.prototype.submit = function () {
    try {
      reportFormSubmit(this, callback);
    } catch (e) {
      // ignore errors
    }
    originalSubmit.call(this);
  };

  // Observe documents and collect cleanup function
  const observerCleanup = observeDocuments(handleDoc);

  // Combined cleanup function
  return () => {
    observerCleanup();
    HTMLFormElement.prototype.submit = originalSubmit;
  };
}
